import { SmDrawer } from '@smiles/smiles-react-design-system';
import React, { useCallback, useEffect, useState } from 'react';

import useEvent from '@/hooks/useEvent';
import useApi from '@/hooks/useApi';

import { getContent } from '@/services/requests/Liferay';
import { TOptinContent } from '@/services/requests/Liferay/types';

import { ScreenForm } from './Screen/Form';
import { ScreenSuccess } from './Screen/Success';
import { ScreenError } from './Screen/Error';
import { ScreenTerms } from './Screen/Terms';
import { ScreenWarning } from './Screen/Warning';
import { ScreenLoading } from './Screen/Loading';

export type TScreens =
  | 'form'
  | 'success'
  | 'error'
  | 'terms'
  | 'warning'
  | 'loading';

export function Modal() {
  const [screen, setScreen] = useState<TScreens>('loading');
  const [data, setData] = useState<TOptinContent>({} as TOptinContent);

  const { ApiLiferay, setTraceId } = useApi();
  const event = useEvent();

  const fetchData = useCallback(async (key: string) => {
    try {
      const result = await getContent(ApiLiferay, key);
      setData(result as TOptinContent);
      setScreen('form');
    } catch (error) {
      setScreen('error');
    }
  }, []);

  const handlerBack = useCallback(() => {
    setScreen('form');
  }, []);

  const handlerTryAgain = useCallback(() => {
    setScreen('loading');
    fetchData(event.data.id);
  }, []);

  const handlerRedirect = useCallback((value: TScreens) => {
    setScreen(value);
  }, []);

  const handlerClose = useCallback(() => {
    setScreen('loading');
    event.close();
  }, []);

  useEffect(() => {
    if (event.data.id && event.isOpen) {
      fetchData(event.data.id);
    }
  }, [event.data.id, event.isOpen, fetchData]);

  useEffect(() => {
    setTraceId(event.data.traceId);
  }, [event, setTraceId]);

  const renderContent = () => {
    const screens = {
      form: (
        <ScreenForm
          data={data}
          onClose={handlerClose}
          onRedirect={handlerRedirect}
        />
      ),
      success: <ScreenSuccess data={data} onClose={handlerClose} />,
      error: (
        <ScreenError
          data={data}
          onClose={handlerClose}
          onBack={handlerBack}
          onTryAgain={handlerTryAgain}
        />
      ),
      terms: (
        <ScreenTerms
          data={data}
          onBack={handlerBack}
          onRedirect={handlerRedirect}
        />
      ),
      warning: (
        <ScreenWarning
          data={data}
          onClose={handlerClose}
          onBack={handlerBack}
        />
      ),
      loading: <ScreenLoading onClose={handlerClose} />,
    };
    return screens[screen];
  };

  return (
    <>
      <SmDrawer
        className="smls-optin__modal"
        isOpen={event.isOpen}
        onClose={handlerClose}
      >
        {event.isOpen && renderContent()}
      </SmDrawer>
    </>
  );
}
